import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTicketApi } from "@/api/tickets";
import type { NavigationGroup } from "./navigation-model";

/** 超过三位数就不再显示精确值，否则角标会把菜单项标题挤掉。 */
function formatBadge(count: number | undefined): string | undefined {
  if (count === undefined || count === 0) {
    return undefined;
  }
  return count > 99 ? "99+" : String(count);
}

/**
 * 给声明了 `countKey` 的菜单项填入实时工单数量。
 * 一次 counts 请求就能覆盖全部视图角标；不带筛选条件，口径即各入口标签的字面含义。
 */
export function useNavigationBadges(groups: NavigationGroup[]): NavigationGroup[] {
  const ticketApi = useTicketApi();
  const hasBadges = groups.some((group) => group.items.some((item) => item.countKey));

  const { data } = useQuery({
    queryKey: ["tickets", "counts", "views"],
    queryFn: ({ signal }) => ticketApi.getViewCounts({}, signal),
    // 权限过滤后一个带角标的入口都不剩时不必发这个请求（服务端也会 403）。
    enabled: hasBadges,
  });

  return useMemo(() => {
    if (!data) {
      return groups;
    }

    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => (item.countKey ? { ...item, badge: formatBadge(data[item.countKey]) } : item)),
    }));
  }, [groups, data]);
}
